import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
} from "recharts";
import {
    Users, Send, Handshake, CheckCircle2, Leaf, AlertTriangle,
    Calendar as CalendarIcon, Clock, Flag, Flame, ArrowRight, DollarSign, FileText,
    Target, Trophy, Plus,
} from "lucide-react";
import { COLORS as GOAL_COLORS, METRICS as GOAL_METRICS } from "@/components/GoGetFormDialog";

const GOAL_COLOR_MAP = Object.fromEntries(GOAL_COLORS.map((c) => [c.key, c]));
const GOAL_IS_MONEY = Object.fromEntries(GOAL_METRICS.map((m) => [m.key, m.isMoney]));

const STATUS_COLORS = {
    Target: "#9CA3AF",
    Outreach: "#3B82F6",
    Connected: "#6366F1",
    Active: "#10B981",
    Nurture: "#F59E0B",
};

function fmtMoney(n) {
    if (!n) return "$0";
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
    return `$${n}`;
}

function MetricCard({ label, value, icon: Icon, accent = "blue", testid, sub }) {
    const tints = {
        blue: { bg: "bg-blue-50", icon: "text-blue-600" },
        emerald: { bg: "bg-emerald-50", icon: "text-emerald-600" },
        indigo: { bg: "bg-indigo-50", icon: "text-indigo-600" },
        amber: { bg: "bg-amber-50", icon: "text-amber-600" },
        rose: { bg: "bg-rose-50", icon: "text-rose-600" },
        slate: { bg: "bg-slate-100", icon: "text-slate-600" },
    };
    const t = tints[accent] || tints.blue;
    return (
        <div className="ph-card p-5 hover:shadow-ph-soft transition-shadow" data-testid={testid}>
            <div className={`w-9 h-9 rounded-lg ${t.bg} flex items-center justify-center mb-4`}>
                <Icon className={`w-4 h-4 ${t.icon}`} strokeWidth={2} />
            </div>
            <div className="ph-label">{label}</div>
            <div className="mt-1 text-2xl font-semibold text-ph-text tabular-nums">{value}</div>
            {sub && <div className="mt-1 text-xs text-ph-mutedSoft">{sub}</div>}
        </div>
    );
}

function StatRow({ label, value, accent }) {
    return (
        <div className="flex items-center justify-between py-2 border-b border-ph-borderSoft last:border-0">
            <div className="text-sm text-ph-muted">{label}</div>
            <div className={`text-sm font-semibold tabular-nums ${accent || "text-ph-text"}`}>{value}</div>
        </div>
    );
}

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [goals, setGoals] = useState([]);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
        api.get("/go-gets").then((r) => setGoals(r.data)).catch(() => setGoals([]));
    }, []);

    if (!data) {
        return (
            <div className="p-8 text-ph-muted text-sm">Loading dashboard…</div>
        );
    }

    const empty = data.totals.total_brokers === 0;
    const statusData = Object.entries(data.status_breakdown).map(([k, v]) => ({ name: k, value: v }));
    const tierData = Object.entries(data.tier_breakdown).map(([k, v]) => ({ name: `Tier ${k}`, value: v }));

    return (
        <div className="p-6 lg:p-8 space-y-6 animate-fade-in" data-testid="dashboard-page">
            {/* Header */}
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="ph-h1">Pipeline overview</h1>
                    <p className="text-sm text-ph-muted mt-1">
                        Track every broker, log every touch, run your weekly review — all in one place.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => nav("/app/activity?log=1")}
                        className="h-9 px-3 rounded-md border border-ph-border bg-white hover:bg-ph-canvas text-sm font-medium text-ph-text transition-colors"
                        data-testid="dashboard-log-activity-btn"
                    >
                        Log activity
                    </button>
                    <button
                        onClick={() => nav("/app/brokers?add=1")}
                        className="h-9 px-3 rounded-md bg-ph-primary text-white text-sm font-medium hover:bg-ph-primaryHover transition-colors"
                        data-testid="dashboard-add-broker-btn"
                    >
                        Add broker
                    </button>
                </div>
            </header>

            {empty && (
                <div className="ph-card border-dashed p-8 text-center">
                    <div className="text-sm font-medium text-ph-text">Your pipeline is empty</div>
                    <div className="text-sm text-ph-muted mt-1">Add brokers to the tracker and start logging touches.</div>
                </div>
            )}

            {/* Top metrics */}
            <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <MetricCard label="Total Brokers" value={data.totals.total_brokers} icon={Users} accent="blue" testid="kpi-total-brokers" />
                <MetricCard label="In Outreach" value={data.totals.in_outreach} icon={Send} accent="indigo" testid="kpi-in-outreach" />
                <MetricCard label="Connected" value={data.totals.connected} icon={Handshake} accent="indigo" testid="kpi-connected" />
                <MetricCard label="Active" value={data.totals.active} icon={CheckCircle2} accent="emerald" testid="kpi-active" />
                <MetricCard label="Nurture" value={data.totals.nurture} icon={Leaf} accent="amber" testid="kpi-nurture" />
                <MetricCard label="Overdue" value={data.totals.overdue} icon={AlertTriangle} accent="rose" testid="kpi-overdue" sub="Follow-ups past due" />
            </section>

            {/* Go Gets section */}
            <GoGetsStrip goals={goals} nav={nav} />

            {/* Weekly + Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <section className="ph-card p-5 lg:col-span-1">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="ph-h2">Weekly activity</h2>
                        <div className="flex items-center gap-2">
                            {data.weekly.source === "kpi_sheet" ? (
                                <span
                                    className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                                        data.weekly.is_current_week
                                            ? "bg-emerald-50 text-emerald-700"
                                            : "bg-amber-50 text-amber-700"
                                    }`}
                                    title={
                                        data.weekly.is_current_week
                                            ? "Pulled from this week's Weekly KPI row"
                                            : "Pulled from your most recent Weekly KPI row — log a new row to update this panel"
                                    }
                                >
                                    {data.weekly.is_current_week ? "KPI · live" : "KPI · last week"}
                                </span>
                            ) : (
                                <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600" title="Auto-tracked from broker activities (no Weekly KPI rows yet)">
                                    Auto-tracked
                                </span>
                            )}
                            <span className="text-[11px] text-ph-mutedSoft">Week of {data.weekly.week_start}</span>
                        </div>
                    </div>
                    <div className="space-y-0">
                        <StatRow label="New target brokers" value={data.weekly.new_target_brokers} />
                        <StatRow label="Touches logged" value={data.weekly.touches_logged} />
                        <StatRow label="Calls made" value={data.weekly.calls_made} />
                        <StatRow label="Conversations held" value={data.weekly.conversations_held} />
                        <StatRow label="New active brokers" value={data.weekly.new_active_brokers} />
                        <StatRow label="Submissions ($25k+)" value={data.weekly.submissions_25k_plus} />
                    </div>
                    {data.weekly.source === "kpi_sheet" && !data.weekly.is_current_week && (
                        <button
                            onClick={() => nav("/app/weekly-kpis")}
                            className="mt-4 w-full text-xs text-ph-primary font-medium hover:text-ph-primaryHover border border-blue-100 bg-ph-primarySoft rounded-md px-3 py-2 transition-colors"
                            data-testid="goto-weekly-kpis-btn"
                        >
                            Add this week's KPI row →
                        </button>
                    )}
                </section>

                <section className="ph-card p-5 lg:col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="ph-h2">Conversion & revenue</h2>
                        <span className="text-[11px] text-ph-mutedSoft">Cumulative</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                        <SubMetric label="Outreach started" value={data.funnel.outreach_started} icon={Send} />
                        <SubMetric label="Conversations" value={data.funnel.conversations_to_date} icon={Handshake} />
                        <SubMetric label="Active" value={data.funnel.active_brokers} icon={CheckCircle2} />
                        <SubMetric label="With submissions" value={data.funnel.brokers_with_submissions} icon={FileText} />
                        <SubMetric label="Est. premium" value={fmtMoney(data.funnel.est_premium_pipeline)} icon={DollarSign} />
                        <SubMetric label="Bound premium" value={fmtMoney(data.funnel.bound_premium_total)} icon={DollarSign} />
                    </div>
                    <div className="space-y-3">
                        {[
                            ["Outreach → Conversation", data.funnel.outreach_to_conversation_pct, data.funnel.outreach_started, data.funnel.conversations_to_date],
                            ["Conversation → Active", data.funnel.conversation_to_active_pct, data.funnel.conversations_to_date, data.funnel.active_brokers],
                            ["Active → Submission", data.funnel.active_to_submission_pct, data.funnel.active_brokers, data.funnel.brokers_with_submissions],
                        ].map(([label, pct, from, to]) => (
                            <div key={label}>
                                <div className="flex justify-between items-center text-xs text-ph-muted mb-1.5">
                                    <span className="font-medium text-ph-text">{label}</span>
                                    <span className="tabular-nums">
                                        <span className="text-ph-primary font-semibold">{pct}%</span>
                                        <span className="ml-2 text-ph-mutedSoft">{from} → {to}</span>
                                    </span>
                                </div>
                                <div className="h-1.5 rounded-full bg-ph-borderSoft overflow-hidden">
                                    <div className="h-full bg-ph-primary rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            {/* Focus + breakdowns */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <section className="ph-card p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Flame className="w-4 h-4 text-ph-primary" strokeWidth={2} />
                        <h2 className="ph-h2">Focus queue</h2>
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center justify-between py-2 border-b border-ph-borderSoft">
                            <span className="text-sm text-ph-muted flex items-center gap-1.5">
                                <CalendarIcon className="w-3.5 h-3.5" /> Due today
                            </span>
                            <span className="text-sm font-semibold tabular-nums">{data.focus_queue.due_today.length}</span>
                        </div>
                        {data.focus_queue.due_today.slice(0, 5).map((b) => (
                            <button
                                key={b.id}
                                onClick={() => nav(`/app/brokers?broker=${b.id}`)}
                                data-testid={`due-today-row-${b.id}`}
                                className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-ph-canvas flex items-center justify-between group"
                            >
                                <span className="truncate text-ph-text">{b.agency_name}</span>
                                <span className="text-ph-primary flex items-center gap-0.5 opacity-70 group-hover:opacity-100">
                                    {b.next_action} <ArrowRight className="w-3 h-3" />
                                </span>
                            </button>
                        ))}
                        <StatRow label={<span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Due in next 7 days</span>} value={data.focus_queue.due_in_7_days} />
                        <StatRow label="Missing next action" value={data.focus_queue.missing_next_action} />
                        <StatRow label="Stale 30+ days" value={data.focus_queue.stale_30_plus} />
                        <StatRow label="Tier 1 active" value={data.focus_queue.tier_1_active} />
                        <StatRow label="Avg priority score" value={data.focus_queue.avg_priority_score} accent="text-ph-primary" />
                    </div>
                </section>

                <section className="ph-card p-5">
                    <h2 className="ph-h2 mb-3">Status breakdown</h2>
                    <div className="h-44">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={42} outerRadius={72} paddingAngle={2}>
                                    {statusData.map((s) => (
                                        <Cell key={s.name} fill={STATUS_COLORS[s.name] || "#888"} stroke="white" strokeWidth={2} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                        {statusData.map((s) => (
                            <div key={s.name} className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[s.name] }} />
                                <span className="text-ph-muted">{s.name}</span>
                                <span className="font-semibold ml-auto tabular-nums text-ph-text">{s.value}</span>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="ph-card p-5">
                    <h2 className="ph-h2 mb-3">Tier breakdown</h2>
                    <div className="h-44">
                        <ResponsiveContainer>
                            <BarChart data={tierData}>
                                <XAxis dataKey="name" stroke="#9CA3AF" tickLine={false} axisLine={false} fontSize={11} />
                                <YAxis stroke="#9CA3AF" tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} />
                                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} cursor={{ fill: "#F1F5F9" }} />
                                <Bar dataKey="value" fill="#3B82F6" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </section>
            </div>

            {/* Operating Rhythm */}
            <section className="ph-card p-6">
                <div className="flex items-center gap-2 mb-3">
                    <Flag className="w-4 h-4 text-ph-primary" strokeWidth={2} />
                    <h2 className="ph-h2">Operating rhythm</h2>
                </div>
                <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
                    {[
                        "Add every target to Broker Tracker.",
                        "Log every touch in Activity Log.",
                        "Update stage and milestone dates after each conversation.",
                        "Review overdue follow-ups each Friday.",
                        "Use Priority Score to focus on highest-value relationships.",
                    ].map((line, i) => (
                        <li key={i} className="rounded-lg border border-ph-borderSoft bg-ph-canvas p-3">
                            <div className="text-[11px] font-semibold text-ph-primary mb-1">Step {i + 1}</div>
                            <div className="text-xs text-ph-text leading-snug">{line}</div>
                        </li>
                    ))}
                </ol>
            </section>
        </div>
    );
}

function SubMetric({ label, value, icon: Icon }) {
    return (
        <div className="rounded-lg border border-ph-borderSoft bg-ph-canvas p-3">
            <div className="flex items-center gap-1.5 ph-label">
                <Icon className="w-3 h-3" />
                {label}
            </div>
            <div className="text-base font-semibold text-ph-text mt-0.5 tabular-nums">{value}</div>
        </div>
    );
}

function GoGetsStrip({ goals, nav }) {
    const fmtMoneyLocal = (n) => {
        if (!n && n !== 0) return "$0";
        if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
        if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
        return `$${Math.round(n).toLocaleString()}`;
    };
    const fmtVal = (metric, n) => GOAL_IS_MONEY[metric] ? fmtMoneyLocal(n) : Math.round(n || 0).toLocaleString();

    if (!goals || goals.length === 0) {
        return (
            <section className="ph-card p-5" data-testid="dashboard-go-gets-empty">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-ph-primarySoft flex items-center justify-center">
                            <Target className="w-5 h-5 text-ph-primary" strokeWidth={2} />
                        </div>
                        <div>
                            <h2 className="ph-h2">Go Gets</h2>
                            <p className="text-xs text-ph-muted mt-0.5">No goals yet — set one and the team sees progress every time they open the dashboard.</p>
                        </div>
                    </div>
                    <button
                        onClick={() => nav("/app/go-gets")}
                        className="h-9 px-3 rounded-md bg-ph-primary text-white text-sm font-medium hover:bg-ph-primaryHover transition-colors"
                        data-testid="dashboard-add-go-get-btn"
                    >
                        <Plus className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Add Go Get
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="ph-card p-5" data-testid="dashboard-go-gets">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-ph-primary" strokeWidth={2} />
                    <h2 className="ph-h2">Go Gets</h2>
                    <span className="text-[11px] text-ph-mutedSoft">{goals.length} {goals.length === 1 ? "goal" : "goals"}</span>
                </div>
                <button
                    onClick={() => nav("/app/go-gets")}
                    className="text-xs text-ph-primary font-medium hover:text-ph-primaryHover flex items-center gap-1"
                    data-testid="dashboard-view-all-go-gets"
                >
                    View all <ArrowRight className="w-3 h-3" />
                </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {goals.slice(0, 6).map((g) => {
                    const c = GOAL_COLOR_MAP[g.color] || GOAL_COLOR_MAP.blue;
                    const pct = Math.min(100, g.progress_pct || 0);
                    const achieved = (g.progress_pct || 0) >= 100;
                    return (
                        <button
                            key={g.id}
                            onClick={() => nav("/app/go-gets")}
                            data-testid={`dashboard-go-get-${g.id}`}
                            className="relative text-left rounded-lg border border-ph-borderSoft p-4 hover:shadow-ph-card transition-all hover:border-ph-border bg-white overflow-hidden"
                        >
                            <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: c.solid }} />
                            <div className="pl-2.5">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <div className="text-sm font-semibold text-ph-text leading-tight truncate" title={g.name}>{g.name}</div>
                                    {achieved && <Trophy className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />}
                                </div>
                                <div className="flex items-baseline justify-between text-xs mb-2">
                                    <span className="text-ph-text font-medium tabular-nums">
                                        {fmtVal(g.metric, g.current_value)} <span className="text-ph-mutedSoft font-normal">/ {fmtVal(g.metric, g.target)}</span>
                                    </span>
                                    <span
                                        className="font-semibold tabular-nums px-1.5 py-0.5 rounded"
                                        style={{ background: c.soft, color: c.text }}
                                    >
                                        {(g.progress_pct || 0).toFixed(0)}%
                                    </span>
                                </div>
                                <div className="h-1.5 rounded-full bg-ph-borderSoft overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: c.solid }} />
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
